import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const [debugConfig, setDebugConfig] = useState(null);

  // Token is now handled by the api service interceptor
  useEffect(() => {
    if (token) {
      console.log('Token set in localStorage:', token.substring(0, 20) + '...');
    } else {
      console.log('Token removed from localStorage');
    }
  }, [token]);

  const fetchDebugConfig = async () => {
    try {
      const response = await api.get('/api/debug/config');
      setDebugConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch debug config:', error);
      // Set default config if fetch fails
      setDebugConfig({ debug_fast_answers: false });
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/api/dashboard');
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      logout();
    }
    setLoading(false);
  };

  // Fetch debug config on mount (no auth required)
  useEffect(() => {
    fetchDebugConfig();
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/login', {
        email,
        password
      });

      const { access_token, user } = response.data;

      setToken(access_token);
      setUser(user);
      localStorage.setItem('authToken', access_token);

      return { success: true, user };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';

      // Clear any existing tokens when login fails
      setToken(null);
      setUser(null);
      localStorage.removeItem('authToken');

      return { success: false, error: message };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await api.post('/api/signup', userData);
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Signup failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    delete api.defaults.headers.common['Authorization'];
  };

  const updateUser = (userData) => {
    setUser(prev => ({
      ...prev,
      ...userData
    }));
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/api/dashboard');
      if (response.data.user) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const value = {
    user,
    token,
    loading,
    debugConfig,
    login,
    signup,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};